import { useCopyToClipboard } from 'Modules/softphone/components/SoftphoneDevTools/hooks';
import { Check, Copy } from 'lucide-react';
import type { SipConfigRowProps } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/SipTab/SipConfigRow/index.types';
import {
  BUTTON,
} from 'Modules/softphone/components/SoftphoneDevTools/styles';
import React, { useState } from 'react';
import { cn } from '~/lib/utils';

/**
 * SipConfigRow - One configuration value, copyable on its own
 *
 * Values are copyable individually because the thing a report needs is
 * usually one of them — the WSS URI, the auth username — and selecting text
 * out of a 570px panel with truncated rows does not work.
 */
export const SipConfigRow: React.FC<SipConfigRowProps> = ({
  label,
  value,
  secret = false,
}) => {
  const { copied, copy } = useCopyToClipboard();
  const [revealed, setRevealed] = useState(false);

  const shown =
    secret && !revealed
      ? '•'.repeat(Math.min(value?.length ?? 0, 12)) || 'N/A'
      : (value ?? 'N/A');

  return (
    <div className="flex min-h-[30px] items-center gap-2.5 border-b border-zinc-800 px-3 py-[9px] last:border-b-0">
      <span className="w-[130px] flex-shrink-0 text-[11.5px] text-zinc-400">
        {label}
      </span>
      <span
        className={cn(
          'min-w-0 flex-1 truncate text-right font-sp-mono text-[11.5px]',
          secret && !revealed ? 'text-zinc-300' : 'text-zinc-200'
        )}
        title={secret && !revealed ? undefined : (value ?? undefined)}
      >
        {shown}
      </span>

      {secret ? (
        <button
          type="button"
          onClick={() => setRevealed((open) => !open)}
          className="flex-shrink-0 rounded-[5px] border border-zinc-800 px-[7px] py-[3px] text-[10.5px] text-zinc-400 transition-colors hover:border-zinc-700 hover:text-zinc-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/40"
        >
          {revealed ? 'Hide' : 'Reveal'}
        </button>
      ) : (
        <button
          type="button"
          onClick={() => copy(value)}
          disabled={!value}
          aria-label={`Copy ${label}`}
          className={cn(BUTTON.icon, 'disabled:opacity-40')}
        >
          {copied ? (
            <Check className="h-2.5 w-2.5 text-emerald-400" />
          ) : (
            <Copy className="h-2.5 w-2.5" />
          )}
        </button>
      )}
    </div>
  );
};
